import React, { useState, useRef } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { accessibilityPropCheck } from 'chameleon/ui-stack/utilities/utils';
import A11y from 'chameleon/ui-stack/utilities/a11y';

import {
  keyNames,
  tabsArrowHandler,
  tabsTabKeyHandler,
} from 'chameleon/legacy/src/utility/accessibility';

const Tabs = React.forwardRef(
  (
    { tabs, direction, classNames, accessibility, tabsClassName, initActive },
    ref,
  ) => {
    const [activeTab, setActive] = useState(initActive);
    const tabRefs = useRef(Array(tabs.length).fill(null));

    const classes = cn([
      'flex',
      direction === 'horizontal' ? 'flex-row' : 'flex-col',
      tabsClassName,
    ]);

    const { ariaLabel, ariaLabelledBy, title } = accessibility;

    const accessibilityProps = {
      title,
      role: 'tablist',
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-orientation': direction,
    };

    return (
      <div ref={ref} className={classes} {...accessibilityProps}>
        {tabs.map(
          (
            {
              name,
              ariaControls,
              onClick,
              isActive: isTabActive = undefined,
              ariaLabel,
              otherProps,
            },
            index,
          ) => {
            /**
             * if isActive is not defined, use the index to determine activeness, otherwise
             * use the isActive prop that comes with the tab
             */
            const isActive =
              (isTabActive === undefined && index === activeTab) || isTabActive;
            const tabStyle = cn([
              classNames.tab,
              'cursor-pointer',
              isActive && classNames.tabActive,
              !isActive && classNames.tabInactive,
            ]);
            const titleStyle = cn([
              classNames.title,
              isActive && classNames.titleActive,
            ]);
            return (
              <A11y key={index} onKeyDown={handleKeyDown}>
                <div
                  role="tab"
                  aria-label={ariaLabel || name}
                  aria-controls={ariaControls}
                  aria-selected={isActive ? 'true' : 'false'}
                  tabIndex={isActive ? '0' : '-1'}
                  className={tabStyle}
                  onClick={() => {
                    setActive(index);
                    onClick(index);
                  }}
                  ref={(el) => (tabRefs.current[index] = el)}
                  {...otherProps}
                >
                  <div className={titleStyle}>{name}</div>
                </div>
              </A11y>
            );
          },
        )}
      </div>
    );
  },
);

const handleKeyDown = (e) => {
  if (
    [
      keyNames.arrowLeft,
      keyNames.arrowRight,
      keyNames.home,
      keyNames.end,
    ].includes(e.key)
  ) {
    tabsArrowHandler(e);
    return;
  }

  if (e.key === keyNames.tab) {
    tabsTabKeyHandler(e);
  }
};

Tabs.propTypes = {
  direction: PropTypes.oneOf(['horizontal', 'vertical']),
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.node,
      onClick: PropTypes.func,
      isActive: PropTypes.bool,
      ariaControls: PropTypes.string,
    }).isRequired,
  ).isRequired,
  accessibility: accessibilityPropCheck,
  initActive: PropTypes.number,
};

Tabs.defaultProps = {
  direction: 'horizontal',
  accessibility: {
    title: 'Tab',
    'aria-label': '',
    'aria-labelledby': '',
  },
  tabs: [],
  initActive: 0,
};

export default Tabs;
